import type {
  LatLng,
  OsmPoiResponse,
  Radar,
  Roadwork,
  SpeedLimit,
} from './types';
import { config } from './config';
import { bearingDegrees, haversineMeters } from './routing';
import { yieldToMain } from './mainThread';

/**
 * Speed-camera + speed-limit + roadwork layer.
 *
 *  - Single Overpass query per 1 km² grid cell (cache TTL 30 min) — keeps us
 *    polite to overpass-api.de which is rate-limited per source IP. All three
 *    POI kinds ride on the SAME request so we never add an upstream hit.
 *  - The browser talks to the Cloudflare Worker proxy at `/v2/overpass`
 *    (see `functions/v2/[[path]].ts`), the Worker forwards POST (with
 *    `application/x-www-form-urlencoded` body) to the upstream.
 *  - One in-flight request at a time: a rapid movement across grid cells
 *    aborts the older request before starting the new one.
 */

/** Search radius (Overpass `around:`). 2 km is plenty for "ahead-of-me" alerts. */
const QUERY_RADIUS_M = 2000;
/** On-road limit: distance to way geometry (GPS + lane jitter). */
const ACTIVE_LIMIT_RADIUS_M = 90;
/** Fallback when a way has only a centroid (no geometry in the payload). */
const ACTIVE_LIMIT_CENTER_RADIUS_M = 700;
/** Camera warning only fires for cameras within this distance. */
const ALERT_RADIUS_M = 350;
/** Roadwork warning only fires for features within this distance. Slightly
 *  larger than the radar radius because OSM construction stretches over
 *  hundreds of metres and the user's first visual cue should arrive
 *  earlier. */
const ROADWORK_ALERT_RADIUS_M = 1500;
/** Cache TTL — Overpass is rate-limited; don't spam. */
const TTL_MS = 30 * 60 * 1000;
/** Horizontal voxel resolution for the bin-caching key. 1/50 deg ≈ 2.2 km. */
const GRID_SCALE = 50;
/** Hard cap on the grid cache so a long drive doesn't leak memory. */
const MAX_CACHE_ENTRIES = 30;

interface CacheEntry {
  ts: number;
  data: OsmPoiResponse | null;
}

const cache = new Map<string, CacheEntry>();
let inFlight: AbortController | null = null;

function overpassUrl(): string {
  if (config.mode === 'proxy') return `${config.proxyUrl}/v2/overpass`;
  return '/v2/overpass';
}

/** FIFO eviction when the cache grows past MAX_CACHE_ENTRIES. */
function pruneCache(): void {
  while (cache.size > MAX_CACHE_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

function gridKey(p: LatLng): string {
  return `g2:${Math.round(p.lat * GRID_SCALE)},${Math.round(p.lng * GRID_SCALE)}`;
}

/**
 * Parse an OSM `maxspeed` tag → km/h. Returns null for unrecognised values
 * so the caller can fall back to a "—" placeholder.
 *
 * Recognised formats:
 *  - "50", "80", "120"          (implicit km/h)
 *  - "50 mph", "30 mph"          (converted to km/h)
 *  - "RO:urban", "DE:rural",     (EU country-coded defaults — limited set)
 *  - anything else → null
 */
export function parseMaxspeedKmh(tag: string | undefined): number | null {
  if (!tag) return null;
  const trimmed = tag.trim().toLowerCase();
  const mph = trimmed.match(/^(\d+(?:\.\d+)?)\s*mph$/);
  if (mph) return Math.round(parseFloat(mph[1]!) * 1.60934);
  const kmh = trimmed.match(/^(\d+(?:\.\d+)?)\s*(?:km\/h|kmh)?$/);
  if (kmh) {
    const v = parseFloat(kmh[1]!);
    return Number.isFinite(v) && v > 0 ? v : null;
  }
  // EU country-coded defaults — small subset that covers most OSM entries.
  const coded: Record<string, number> = {
    'ro:urban': 50,
    'ro:rural': 90,
    'de:urban': 50,
    'de:rural': 100,
    'de:motorway': 130,
    'hu:urban': 50,
    'hu:rural': 90,
    'hu:trunk': 110,
    'hu:motorway': 130,
    'at:urban': 50,
    'at:rural': 100,
    'at:trunk': 100,
    'at:motorway': 130,
    'fr:urban': 50,
    'fr:rural': 80,
    'cz:urban': 50,
    'cz:rural': 90,
    'sk:urban': 50,
    'sk:rural': 90,
    'pl:urban': 50,
    'pl:rural': 90,
  };
  return coded[trimmed] ?? null;
}

function wayMaxspeedTag(
  tags:
    | {
        maxspeed?: string;
        'maxspeed:forward'?: string;
        'maxspeed:backward'?: string;
      }
    | undefined,
): string | undefined {
  if (!tags) return undefined;
  return tags.maxspeed || tags['maxspeed:forward'] || tags['maxspeed:backward'];
}

function wayAnchor(el: {
  center?: { lat: number; lon: number };
  geometry?: Array<{ lat: number; lon: number }>;
}): LatLng | null {
  if (el.center) return { lat: el.center.lat, lng: el.center.lon };
  const geom = el.geometry;
  if (!geom || geom.length === 0) return null;
  const mid = geom[Math.floor(geom.length / 2)]!;
  return { lat: mid.lat, lng: mid.lon };
}

/** Metres from point to closest segment of an OSM way geometry. */
function distanceToWayGeometryMeters(
  here: LatLng,
  geometry: Array<{ lat: number; lon: number }>,
): number {
  if (geometry.length === 0) return Infinity;
  if (geometry.length === 1) {
    return haversineMeters(here, { lat: geometry[0]!.lat, lng: geometry[0]!.lon });
  }
  let best = Infinity;
  for (let i = 1; i < geometry.length; i++) {
    const a = geometry[i - 1]!;
    const b = geometry[i]!;
    const d = distancePointToSegmentMeters(
      here,
      { lat: a.lat, lng: a.lon },
      { lat: b.lat, lng: b.lon },
    );
    if (d < best) best = d;
  }
  return best;
}

function distancePointToSegmentMeters(p: LatLng, a: LatLng, b: LatLng): number {
  const midLat = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  const cos = Math.cos(midLat);
  const ax = a.lng * cos;
  const ay = a.lat;
  const bx = b.lng * cos;
  const by = b.lat;
  const px = p.lng * cos;
  const py = p.lat;
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-18) return haversineMeters(p, a);
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = ax + t * dx;
  const qy = ay + t * dy;
  return haversineMeters(p, { lat: qy, lng: qx / cos });
}

/**
 * Fetch nearby speed cameras + speed limits around `p`. Cached per 1 km
 * grid cell. Returns `null` on network error / 5xx (also cached briefly
 * so we don't hammer Overpass while a connection is down).
 */
export async function fetchNearbyPois(p: LatLng): Promise<OsmPoiResponse | null> {
  const key = gridKey(p);
  const hit = cache.get(key);
  const now = Date.now();
  if (hit && now - hit.ts < TTL_MS) return hit.data;

  // Cancel any in-flight before starting a new one — only one Overpass
  // request can be running at a time even on rapid movement.
  if (inFlight) inFlight.abort();
  const ctl = new AbortController();
  inFlight = ctl;

  // All three POI kinds ride on ONE upstream request. Roadwork queries
  // intentionally skip `way["access"="no"]` because it over-matches on
  // vehicle-restricted side streets (pedestrian zones) that aren't
  // really roadworks.
  const query = `[out:json][timeout:25];(node["highway"="speed_camera"](around:${QUERY_RADIUS_M},${p.lat},${p.lng});way["maxspeed"](around:${QUERY_RADIUS_M},${p.lat},${p.lng});way["maxspeed:forward"](around:${QUERY_RADIUS_M},${p.lat},${p.lng});way["highway"="construction"](around:${QUERY_RADIUS_M},${p.lat},${p.lng});way["highway"="closed"](around:${QUERY_RADIUS_M},${p.lat},${p.lng});way["temporary:access"="no"](around:${QUERY_RADIUS_M},${p.lat},${p.lng}););out geom;`;
  try {
    const res = await fetch(overpassUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'data=' + encodeURIComponent(query),
      signal: ctl.signal,
    });
    if (!res.ok) {
      cache.set(key, { ts: now, data: null });
      pruneCache();
      return null;
    }
    const raw = await res.text();
    await yieldToMain();
    const json = JSON.parse(raw) as OsmPoiResponse;
    await yieldToMain();
    cache.set(key, { ts: now, data: json });
    pruneCache();
    return json;
  } catch (err) {
    if ((err as Error).name === 'AbortError') return null;
    cache.set(key, { ts: now, data: null });
    return null;
  } finally {
    if (inFlight === ctl) inFlight = null;
  }
}

/** Pull the speed-camera nodes out of a response. */
export function extractRadars(data: OsmPoiResponse | null): Radar[] {
  if (!data) return [];
  const out: Radar[] = [];
  for (const el of data.elements) {
    if (el.type !== 'node') continue;
    if (el.tags?.highway !== 'speed_camera') continue;
    const kmh = parseMaxspeedKmh(el.tags?.maxspeed);
    const r: Radar =
      kmh !== null
        ? { lat: el.lat, lng: el.lon, maxspeed: kmh }
        : { lat: el.lat, lng: el.lon };
    out.push(r);
  }
  return out;
}

/** Find the speed-limit way closest to `here` (on-road geometry preferred). */
export function extractNearestSpeedLimit(
  data: OsmPoiResponse | null,
  here: LatLng,
): SpeedLimit | null {
  if (!data) return null;
  let best: SpeedLimit | null = null;
  let bestDist = Infinity;
  for (const el of data.elements) {
    if (el.type !== 'way') continue;
    const kmh = parseMaxspeedKmh(wayMaxspeedTag(el.tags));
    if (kmh === null) continue;
    const anchor = wayAnchor(el);
    if (!anchor) continue;
    const geom = el.geometry;
    const d =
      geom && geom.length >= 2
        ? distanceToWayGeometryMeters(here, geom)
        : haversineMeters(here, anchor);
    const maxDist =
      geom && geom.length >= 2 ? ACTIVE_LIMIT_RADIUS_M : ACTIVE_LIMIT_CENTER_RADIUS_M;
    if (d > maxDist) continue;
    if (d < bestDist) {
      bestDist = d;
      best = { kmh, lat: anchor.lat, lng: anchor.lng };
    }
  }
  return best;
}

/**
 * Pick a radar to warn about. When moving (>2 m/s) we require the camera
 * to sit roughly ahead of the velocity vector (within a 120° forward cone).
 * When stationary / paused, we just take the closest.
 */
export function pickRelevantRadar(
  radars: Radar[],
  here: LatLng,
  velocityBearingDeg: number | null,
  speedMps: number | null,
): Radar | null {
  if (radars.length === 0) return null;
  const moving = speedMps !== null && speedMps > 2;
  let best: { r: Radar; d: number } | null = null;
  for (const r of radars) {
    const d = haversineMeters(here, { lat: r.lat, lng: r.lng });
    if (d > ALERT_RADIUS_M) continue;
    if (moving && velocityBearingDeg !== null) {
      const toward = bearingDegrees(here, { lat: r.lat, lng: r.lng });
      // wrap-aware shortest angular difference in [0,180]
      let diff = (toward - velocityBearingDeg + 540) % 360 - 180;
      diff = Math.abs(diff);
      if (diff > 60) continue;
    }
    if (!best || d < best.d) best = { r, d };
  }
  return best ? best.r : null;
}

/**
 * Roadwork / closure classification rules:
 *   - `highway=construction` → 'construction' (most common; OSM tag for
 *     any road physically being rebuilt). `construction=*` is attached as
 *     `futureClass` for the chip text.
 *   - `highway=closed` → 'closure'. Explicitly closed (rare in OSM).
 *   - `temporary:access=no` → 'closure'. OSM convention for temporary
 *     closures (typically roadworks) — often used when `highway=closed`
 *     would be overkill.
 *   - everything else → skipped.
 */
export function extractRoadworks(data: OsmPoiResponse | null): Roadwork[] {
  if (!data) return [];
  const out: Roadwork[] = [];
  for (const el of data.elements) {
    if (el.type !== 'way') continue;
    const tags = el.tags ?? {};
    const anchor = wayAnchor(el);
    if (!anchor) continue;
    if (tags.highway === 'construction') {
      out.push({
        kind: 'construction',
        lat: anchor.lat,
        lng: anchor.lng,
        futureClass: tags.construction,
      });
      continue;
    }
    if (tags.highway === 'closed' || tags['temporary:access'] === 'no') {
      out.push({
        kind: 'closure',
        lat: anchor.lat,
        lng: anchor.lng,
      });
    }
  }
  return out;
}

/**
 * Pick the roadwork most relevant to the current heading. Closure
 * outranks construction when both are within ~10 m of each other —
 * declared as a small safety preference, NOT a hard override. Distance
 * is still the dominant factor. Identical cone logic to `pickRelevantRadar`.
 */
export function pickRelevantRoadwork(
  roadworks: Roadwork[],
  here: LatLng,
  velocityBearingDeg: number | null,
  speedMps: number | null,
): Roadwork | null {
  if (roadworks.length === 0) return null;
  const moving = speedMps !== null && speedMps > 2;
  let best: { r: Roadwork; d: number; rank: number } | null = null;
  for (const r of roadworks) {
    const d = haversineMeters(here, { lat: r.lat, lng: r.lng });
    if (d > ROADWORK_ALERT_RADIUS_M) continue;
    if (moving && velocityBearingDeg !== null) {
      const toward = bearingDegrees(here, { lat: r.lat, lng: r.lng });
      let diff = (toward - velocityBearingDeg + 540) % 360 - 180;
      diff = Math.abs(diff);
      if (diff > 60) continue;
    }
    // Closure preference: a closure up to 10 m behind a construction
    // site still wins. Anything past that 10 m gap is decided by raw
    // distance, which matches user expectation that the closer hazard
    // is the more actionable one.
    const rank = r.kind === 'closure' ? -10 : 0;
    if (!best || d + rank < best.d + best.rank) best = { r, d, rank };
  }
  return best ? best.r : null;
}
